import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'

import { setTracingRequestHeaders } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/requestHeaderConstants'
import { CALENDAR_SERVICE_BASE_URL } from '@lyrahealth-inc/shared-app-logic/src/features/providers/constants'

import { refreshApiToken } from '../../data/authActions'
import * as store from '../../data/store'
import { genAIBaseUrl } from '../constants/appConstants'
import { actionAlertHandler, actionStyles } from '../constants/reduxConstants'
import { logToSentry } from '../utils/utils'

// Auth Instance ------
// set up auth instance, that sends token and listens for 401
const authInstance = axios.create({
  baseURL: '/api',
})
const CancelToken = axios.CancelToken

authInstance.interceptors.request.use(function (config: AxiosRequestConfig) {
  const token = store.default.getState().auth?.apiToken
  if (token && config.headers) {
    config.headers.Authorization = 'JWT ' + token
  }
  if (!config.cancelToken) {
    // allow for preattached cancel tokens, so specific call targeting is possible
    config.cancelToken = new CancelToken(function executor(c) {
      // An executor function receives a cancel function as a parameter
      ;(authInstance as any).cancelLastRequest = c
    })
  }
  setTracingRequestHeaders(config)
  return config
})

authInstance.interceptors.response.use(
  function (response: AxiosResponse) {
    if (response.headers['fresh-token']) {
      store.default.dispatch(refreshApiToken(response.headers['fresh-token']))
    }

    return response
  },
  async function (error: AxiosError) {
    if (error.response && error.response.status === 401) {
      const { logoutMutation } = await import('../../data/authApi')
      store.default.dispatch(logoutMutation.initiate({}))
    }

    if (error.response && error.response.status >= 500) {
      logToSentry('requestError', {
        error: error?.message ?? String(error),
        stack: error?.stack,
        requestUrl: error?.config?.url,
      })
    }

    return Promise.reject(error)
  },
)

export const axiosAuthInstance = authInstance

// Generic Instance --------
// axios instance without any frills
export const axiosGenericInstance = axios.create({ baseURL: '/api' })

axiosGenericInstance.interceptors.request.use(function (config: AxiosRequestConfig) {
  setTracingRequestHeaders(config)
  return config
})

// Calendar service INSTANCE --------------------------------

const calendarServiceInstance = axios.create({
  baseURL: CALENDAR_SERVICE_BASE_URL,
})

calendarServiceInstance.interceptors.request.use(function (config: AxiosRequestConfig) {
  const token = store.default.getState().auth?.apiToken
  if (token && config.headers) {
    config.headers.Authorization = 'Bearer ' + token
  }
  setTracingRequestHeaders(config)
  return config
})

calendarServiceInstance.interceptors.response.use(
  function (response: AxiosResponse) {
    return response
  },
  async function (error: AxiosError) {
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      const { logoutMutation } = await import('../../data/authApi')
      store.default.dispatch(logoutMutation.initiate({}))
    }

    if (error.response && error.response.status === 404) {
      return Promise.reject(error)
    }

    actionAlertHandler({
      actionStyle: actionStyles.ERROR,
      message: error,
      dispatch: store.default.dispatch,
      expires: true,
      action: undefined,
    })

    if (error.response && error.response.status >= 500) {
      logToSentry('calendarServiceRequestError', {
        error: error?.message ?? String(error),
        stack: error?.stack,
        requestUrl: error?.config?.url,
      })
    }

    return Promise.reject(error)
  },
)

export const axiosCalendarServicInstance = calendarServiceInstance

// GenAI Service Instance --------------------------------

const genAIInstance = axios.create({
  baseURL: genAIBaseUrl,
})

genAIInstance.interceptors.request.use(function (config: AxiosRequestConfig) {
  const token = store.default.getState().auth?.apiToken
  if (token && config.headers) {
    config.headers.Authorization = 'bearer ' + token
  }
  return config
})

genAIInstance.interceptors.response.use(
  function (response: AxiosResponse) {
    return response
  },
  async function (error: AxiosError) {
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      const { logoutMutation } = await import('../../data/authApi')
      store.default.dispatch(logoutMutation.initiate({}))
    }

    if (error.response && error.response.status === 404) {
      return Promise.reject(error)
    }

    actionAlertHandler({
      actionStyle: actionStyles.ERROR,
      message: error,
      dispatch: store.default.dispatch,
      expires: true,
      action: undefined,
    })

    if (error.response && error.response.status >= 500) {
      logToSentry('genAIServiceRequestError', {
        error: error?.message ?? String(error),
        stack: error?.stack,
        requestUrl: error?.config?.url,
      })
    }

    return Promise.reject(error)
  },
)

export const axiosGenAIInstance = genAIInstance
